// ChatUIContext.tsx
import React, { createContext, useContext, useMemo, useRef, useState } from "react";

export type AskResult = {
  assistantText: string;
  contextSnapshot?: string;
};

type AskHandler = (instruction: string) => Promise<AskResult | string>;
type ContextProvider = () => string;
type DocApplier = (md: string) => void;

type ChatUIValue = {
  isChatOpen: boolean;
  openChat: () => void;
  closeChat: () => void;
  toggleChat: () => void;

  getContextSnapshot: () => string;

  setInstructionHandler: (fn: AskHandler) => void;
  askInstruction: (instruction: string) => Promise<AskResult>;

  setContextProvider: (fn: ContextProvider) => void;

  setDocApplier: (fn: DocApplier) => void;
  applyDoc: (md: string) => void;

  lastAppliedDoc: string | null;
  setLastAppliedDoc: React.Dispatch<React.SetStateAction<string | null>>;

  loadingResponse: boolean;
  setLoadingResponse: React.Dispatch<React.SetStateAction<boolean>>;
};

const ChatUIContext = createContext<ChatUIValue | null>(null);

export const ChatUIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const handlerRef = useRef<AskHandler | null>(null);
  const contextProviderRef = useRef<ContextProvider | null>(null);
  const [loadingResponse, setLoadingResponse] = useState(false);

  const docApplierRef = useRef<DocApplier | null>(null);

  const [lastAppliedDoc, setLastAppliedDoc] = useState<string | null>(null);

  const value: ChatUIValue = useMemo(
    () => ({
      isChatOpen,
      openChat: () => setIsChatOpen(true),
      closeChat: () => setIsChatOpen(false),
      toggleChat: () => setIsChatOpen((v) => !v),

      getContextSnapshot: () =>
        contextProviderRef.current ? contextProviderRef.current() : "",

      setInstructionHandler: (fn: AskHandler) => {
        handlerRef.current = fn;
      },
      setContextProvider: (fn: ContextProvider) => {
        contextProviderRef.current = fn;
      },

      askInstruction: async (instruction: string) => {
        if (!handlerRef.current) {
          return { assistantText: "No hay handler de instrucción registrado." };
        }
        const res = await handlerRef.current(instruction);
        if (typeof res === "string") return { assistantText: res };
        return res;
      },

      setDocApplier: (fn: DocApplier) => {
        docApplierRef.current = fn;
      },
      applyDoc: (md: string) => {
        docApplierRef.current?.(md);
        setLastAppliedDoc(md);
      },

      lastAppliedDoc,
      setLastAppliedDoc,

      loadingResponse,
      setLoadingResponse,
    }),
    [isChatOpen, loadingResponse, lastAppliedDoc] 
  );

  return <ChatUIContext.Provider value={value}>{children}</ChatUIContext.Provider>;
};

export const useChatUI = () => {
  const ctx = useContext(ChatUIContext);
  if (!ctx) throw new Error("useChatUI debe usarse dentro de un <ChatUIProvider>.");
  return ctx;
};
export const useOptionalChatUI = () => useContext(ChatUIContext);
